import { JSX, useEffect } from "react";
import { CircularProgress, Alert, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCustomerSession } from "src/api/client-api/payment/useCustomerSession";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

type StripePricingTableProps = {
    isPaymentFeatureEnabled: boolean;
};

export const StripePricingTable = ({ isPaymentFeatureEnabled }: StripePricingTableProps): JSX.Element => {
    const { t } = useTranslation();
    const {
        customerSession,
        loading: sessionLoading,
        error: sessionError,
    } = useCustomerSession({
        isPaymentFeatureEnabled,
    });
    const { selectedCompany } = useFoodpilotContext();

    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://js.stripe.com/v3/pricing-table.js";
        script.async = true;
        document.head.appendChild(script);

        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, []);

    if (sessionLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isPaymentFeatureEnabled) {
        return (
            <Alert severity="info">
                {t(
                    "companySettings.payment.feature_disabled",
                    "Payment feature is not enabled. Please contact support.",
                )}
            </Alert>
        );
    }

    const isStripePricingTableSessionError = sessionError || !customerSession;

    if (isStripePricingTableSessionError) {
        return (
            <Alert severity="error">
                {t(
                    "companySettings.payment.pricing_table_error",
                    sessionError ? `Unable to load pricing table: ${sessionError}` : "Unable to load pricing table.",
                )}
            </Alert>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
            {/* @ts-expect-error stripe-pricing-table is not a valid JSX element */}
            <stripe-pricing-table
                pricing-table-id={customerSession.pricingTableId}
                publishable-key={customerSession.publishableKey}
                customer-session-client-secret={customerSession.clientSecret}
                client-reference-id={selectedCompany?.id?.toString()}
            />
        </Box>
    );
};
